"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { User } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { AnalyticsUserRole, setAnalyticsUserContext } from "@/lib/analytics";

interface AppContextType {
  user: User | null;
  loading: boolean;
  location: GeolocationPosition | null;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState<GeolocationPosition | null>(null);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      // Check if user is an artisan without delivery options
      if (user) {
        try {
          const userDocRef = doc(db, "accounts", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userRole: AnalyticsUserRole = userData.isAdmin
              ? "admin"
              : userData.isArtisan
                ? "artisan"
                : "consumer";

            void setAnalyticsUserContext(user.uid, userRole);

            // If user is an artisan and doesn't have delivery options, add them
            if (userData.isArtisan && !userData.deliveryOptions) {
              await updateDoc(userDocRef, {
                deliveryOptions: {
                  delivery: true,
                  deliveryFee: 0,
                  pickup: false,
                  pickupFee: 0,
                },
              });
            }
          } else {
            void setAnalyticsUserContext(user.uid, "consumer");
          }
        } catch (error) {
          console.error("Error checking/updating delivery options:", error);
        }
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Get user location
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => setLocation(position),
        (error) => console.error("Location error:", error),
      );
    }
  }, []);

  const value = {
    user,
    loading,
    location,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
}
